import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import { SubscriptionFilters } from '@/components/features/subscriptions/subscription-filters';
import { SubscriptionTable, type SubscriptionSortColumn } from '@/components/features/subscriptions/subscription-table';
import { TerminateConfirmation } from '@/components/features/subscriptions/terminate-confirmation';
import { useAdminSubscriptions, useTerminateSubscription } from '@/hooks/use-admin-subscriptions';
import { useSortState } from '@/hooks/use-sort-state';
import { sortItems } from '@/lib/utils/sort';
import { filterByDateRange, filterByAmountRange, filterByText, euroToCents } from '@/lib/utils/client-filters';
import type { DateRange } from '@/components/shared/date-range-picker';
import type { ListAdminSubscriptionsParams } from '@/types/admin-subscription';

export function SubscriptionsPage() {
  const { t } = useTranslation('subscriptions');

  // Server-seitige Filter (resetten Cursor)
  const [filters, setFilters] = useState<ListAdminSubscriptionsParams>({ limit: 20 });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // Client-seitige Filter (kein Cursor-Reset)
  const [operatorId, setOperatorId] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Sortierung (client-seitig, Default: Datum desc)
  const { sort, toggleSort } = useSortState<SubscriptionSortColumn>('createdAt', 'desc');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useAdminSubscriptions({
    ...filters,
    cursor: currentCursor,
  });

  const terminateMutation = useTerminateSubscription();

  // Server-Filter Handler (resettet Cursor)
  const handleFiltersChange = useCallback((newFilters: ListAdminSubscriptionsParams) => {
    setFilters(newFilters);
    setCursorStack([]);
  }, []);

  // Date Range Handler (kein Cursor-Reset)
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateFrom(range.from);
    setDateTo(range.to);
  }, []);

  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const handleToggleExpand = useCallback((subscriptionId: string) => {
    setExpandedId((prev) => (prev === subscriptionId ? null : subscriptionId));
  }, []);

  const handleTerminateClick = useCallback((subscriptionId: string) => {
    setTerminatingId(subscriptionId);
  }, []);

  const handleTerminateConfirm = useCallback((reason?: string) => {
    if (!terminatingId) return;
    terminateMutation.mutate(
      { subscriptionId: terminatingId, reason },
      {
        onSuccess: () => {
          setTerminatingId(null);
          setExpandedId(null);
        },
      }
    );
  }, [terminatingId, terminateMutation]);

  // Pipeline: API-Daten → Client-Filter → Sort
  const { filteredItems, totalCount } = useMemo(() => {
    if (!data?.data) return { filteredItems: [], totalCount: 0 };
    const items = data.data;
    const total = items.length;

    let result = filterByDateRange(items, 'createdAt', dateFrom, dateTo);
    result = filterByAmountRange(result, 'grossAmount', euroToCents(amountMin), euroToCents(amountMax));
    result = filterByText(result, 'operatorId', operatorId);
    result = sortItems(result, sort.column, sort.direction);

    return { filteredItems: result, totalCount: total };
  }, [data?.data, dateFrom, dateTo, amountMin, amountMax, operatorId, sort.column, sort.direction]);

  const hasActiveClientFilters = dateFrom || dateTo || amountMin || amountMax || operatorId;
  const showFilterHint = hasActiveClientFilters && filteredItems.length < totalCount;

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common:button.refresh')}
          </Button>
        }
      />

      <SubscriptionFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        operatorId={operatorId}
        onOperatorChange={setOperatorId}
        amountMin={amountMin}
        amountMax={amountMax}
        onAmountMinChange={setAmountMin}
        onAmountMaxChange={setAmountMax}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={handleDateRangeChange}
      />

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={9} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          {showFilterHint && (
            <div className="border-b px-4 py-2 text-sm text-muted-foreground">
              {t('list.filterHint', {
                shown: filteredItems.length,
                total: totalCount,
                defaultValue: 'Zeige {{shown}} von {{total}} Einträgen auf dieser Seite',
              })}
            </div>
          )}
          <SubscriptionTable
            subscriptions={filteredItems}
            expandedId={expandedId}
            onToggleExpand={handleToggleExpand}
            onTerminate={handleTerminateClick}
            isTerminating={terminateMutation.isPending}
            sort={sort}
            onToggleSort={toggleSort}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}

      {terminatingId && (
        <TerminateConfirmation
          subscriptionId={terminatingId}
          open={!!terminatingId}
          onOpenChange={(open) => { if (!open) setTerminatingId(null); }}
          onConfirm={handleTerminateConfirm}
          isPending={terminateMutation.isPending}
        />
      )}
    </div>
  );
}
